/* eslint-disable no-console */
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Form,
  Popconfirm,
  Row,
  Space,
} from 'antd';

import { IRetailerStore, retailerStore } from 'containers/Retailers/Retailer/store';

import { usePatchRequest, usePostRequest, useUserRole } from 'hooks';
import { getIsNew } from 'utils';

import { CreateRetailerRequest, Retailer, UpdateRetailerRequest } from 'codegen';
import { RecursiveRequired } from 'types';

import {
  handleRetailerDetailsPatchRequest,
  handleRetailerDetailsPostRequest,
  handleRetailerRequestError,
  handleSuccessPatchRetailerDetails,
  handleSuccessPostRetailerDetails,
} from './lib';

const { Item, useFormInstance } = Form;

export const RetailerActions = ({ isSaveBtnDisabled }: { isSaveBtnDisabled: boolean; }) => {
  const status = retailerStore((state) => state.status);
  const initialValues = retailerStore((state) => state.initialValues);

  const form = useFormInstance<RecursiveRequired<IRetailerStore['initialValues']>>();

  const navigate = useNavigate();
  const { retailerId } = useParams();

  const [patchRetailerDetails, { loading }] = usePatchRequest<Retailer, UpdateRetailerRequest>({
    url: `/merchants/${retailerId}`,
    options: {
      onError: handleRetailerRequestError(form),
      onSuccess: handleSuccessPatchRetailerDetails,
    },
  });

  const [postRetailerDetails, { loading: postLoading }] = usePostRequest<Retailer, CreateRetailerRequest>({
    url: '/merchants',
    options: {
      onError: handleRetailerRequestError(form),
      onSuccess: handleSuccessPostRetailerDetails(navigate),
    },
  });

  const { isAdmin } = useUserRole();

  const isNew = getIsNew(retailerId);

  const isActive = status === Retailer.status.ACTIVE;
  const isExpired = status === Retailer.status.EXPIRED;
  const enableBtnText = isActive ? 'Disable' : 'Activate';

  if (!isAdmin) {
    return null;
  }

  return (
    <Item
      noStyle
      shouldUpdate
    >
      {() => (
        <Row justify='end' className='mt-6'>
          <Space.Compact>
            {!isNew && !isExpired && (
              <Popconfirm
                title={(
                  <div className='w-[200px]'>
                    Warning! Once disabled, all upcoming receipts from this retailer will not be eligible.
                  </div>
                )}
                okText='Yes'
                cancelText='No'
                placement='bottom'
                disabled={form?.isFieldsTouched()}
                onConfirm={() => patchRetailerDetails({
                  payload: {
                    status: isActive ? UpdateRetailerRequest.status.DISABLED : UpdateRetailerRequest.status.ACTIVE,
                  },
                })}
              >
                <Button
                  danger={isActive}
                  loading={loading || postLoading}
                  disabled={form?.isFieldsTouched()}
                >
                  {enableBtnText}
                </Button>
              </Popconfirm>
            )}

            <Button
              onClick={() => form.resetFields()}
              disabled={!form?.isFieldsTouched()}
            >
              Undo
            </Button>

            <Button
              type='primary'
              loading={loading || postLoading}
              disabled={
                !form?.isFieldsTouched()
                || isSaveBtnDisabled
                || !!form.getFieldsError().some(({ errors }) => !!errors.length)
              }
              onClick={
                isNew
                  ? handleRetailerDetailsPostRequest(form, postRetailerDetails)
                  : handleRetailerDetailsPatchRequest(form, initialValues, patchRetailerDetails)
              }
            >
              Save
            </Button>
          </Space.Compact>
        </Row>
      )}
    </Item>
  );
};
